import { Auth0Lock } from 'auth0-lock';
import { Auth0Error } from 'auth0-js';
import config from '@bymason/runtime-config';
import { callJsonEndpoint } from '@bymason/utils';
import { AuthAdapter, AuthAdapterOptions, UserInfo } from '../types';
import getLockConfig from './get-lock-config';
import jwt_decode from 'jwt-decode';

// see https://auth0.com/docs/libraries/auth0js#polling-with-checksession-
export const AUTH0_MAX_CHECK_SESSION_FREQUENCY = 15 * 60 * 1000; // 15-minutes

export interface IAuth0LockAdapterOptions extends AuthAdapterOptions {
  clientId: string;
  containerId: string;
  domain: string;
  loginCallbackUrl: string;
  returnUrlAfterLogout: string;
}

export class Auth0LockAdapter implements AuthAdapter {
  private readonly options: IAuth0LockAdapterOptions;
  private readonly lock: Auth0LockStatic;

  private _isValidating = false;

  public authToken = '';
  public accessToken = '';
  public customerId = '';
  public userInfo: UserInfo | undefined;

  public get isValidating() {
    return this._isValidating;
  }

  public clearTokens = () => {
    if (this.authToken) {
      this.authToken = '';
      this.accessToken = '';
      this.userInfo = undefined;
    }
  };

  private handleInvalidSession(error?: Auth0Error) {
    this.clearTokens();
    this.options.onInvalidSession(error ? error.description || error.error : undefined);
  }

  private handleValidSession(authResult: AuthResult) {
    let payload: UserInfo;

    try {
      payload = jwt_decode<UserInfo>(authResult.idToken);

      this.authToken = authResult.idToken;
      this.accessToken = authResult.accessToken;
      this.userInfo = payload;
      this.customerId =
        payload.user_metadata && payload.user_metadata.clients ? payload.user_metadata.clients[0] : '';
    } catch (e) {
      console.log('Invalid ID Token: ' + (e as Error).message);
      this.logout();
    }
    this.options.onValidSession(authResult.idToken);
  }

  public logout() {
    this.clearTokens();

    this.lock.logout({
      clientID: this.options.clientId,
      returnTo: this.options.returnUrlAfterLogout,
    });
  }

  public async resetPassword() {
    await callJsonEndpoint({
      method: 'POST',
      url: `https://${config.AuthDomain}/dbconnections/change_password`,
      body: JSON.stringify({
        client_id: config.AuthClientId,
        connection: 'Username-Password-Authentication',
        email: this.userInfo?.email,
      }),
    });
  }

  public showLoginWidget() {
    this.lock.show();
  }

  public validateSession() {
    if (!this._isValidating) {
      /**
       * checkSession will always get a new token
       */
      const checkSession = () => {
        this.lock.checkSession({}, (authError, authResult) => {
          this._isValidating = false;

          if (!authError && authResult) {
            this.handleValidSession(authResult);
          } else {
            this.handleInvalidSession(authError);
          }
        });
      };

      this._isValidating = true;

      if (this.accessToken && this.authToken) {
        /**
         * call getUserInfo here to refresh token if necessary
         * only call checkSession if getUserInfo fails
         */
        this.lock.getUserInfo(this.accessToken, (profileError, profile) => {
          if (profileError || !profile) {
            checkSession();
          } else {
            this._isValidating = false;
          }
        });
      } else {
        checkSession();
      }
    }
  }

  constructor(options: IAuth0LockAdapterOptions) {
    this.options = options;

    const { appName, clientId, containerId, domain, loginCallbackUrl } = options;
    const lockConfig = getLockConfig(appName, containerId, loginCallbackUrl);

    this.lock = new Auth0Lock(clientId, domain, lockConfig);

    this.lock.on('unrecoverable_error', (error) => {
      this.handleInvalidSession(error);
    });

    this.lock.on('authenticated', (authResult) => {
      this.handleValidSession(authResult);
    });

    this.lock.on('authorization_error', (error) => {
      this.handleInvalidSession(error);
    });
  }
}
